import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

function StatsPage() {
  const [stats, setStats] = useState({
    totalVisits: 0,
    uniqueVisitors: 0,
    totalSignups: 0,
    signupsPerVisitor: 0,
    conversionRate: 0,
    conversionRateUnique: 0,
    dailyVisits: [],
    dailySignups: [],
    signupsByProfession: [],
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Total Visits
        const { count: visitsCount, data: visitsData } = await supabase
          .from("visits")
          .select("id, visitor_id, timestamp", { count: "exact" });

        // Unique Visitors
        const uniqueIds = new Set(visitsData.map((v) => v.visitor_id));

        // Total Signups
        const { count: signupsCount, data: signupsData } = await supabase
          .from("signups")
          .select("id, profession, timestamp", { count: "exact" });

        // Signups per Visitor
        const signupsPerVisitor =
          uniqueIds.size > 0 ? signupsCount / uniqueIds.size : 0;

        // Conversion Rate
        const conversionRate =
          visitsCount > 0 ? (signupsCount / visitsCount) * 100 : 0;
        const conversionRateUnique =
          uniqueIds.size > 0 ? (signupsCount / uniqueIds.size) * 100 : 0;

        // Daily visits
        const dailyVisitsMap = {};
        visitsData.forEach((v) => {
          const day = new Date(v.timestamp).toISOString().slice(0, 10);
          dailyVisitsMap[day] = (dailyVisitsMap[day] || 0) + 1;
        });
        const dailyVisits = Object.keys(dailyVisitsMap).map((date) => ({
          date,
          count: dailyVisitsMap[date],
        }));

        // Daily signups
        const dailySignupsMap = {};
        signupsData.forEach((s) => {
          const day = new Date(s.timestamp).toISOString().slice(0, 10);
          dailySignupsMap[day] = (dailySignupsMap[day] || 0) + 1;
        });
        const dailySignups = Object.keys(dailySignupsMap).map((date) => ({
          date,
          count: dailySignupsMap[date],
        }));

        // Signups by profession
        const professionMap = {};
        signupsData.forEach((s) => {
          professionMap[s.profession] = (professionMap[s.profession] || 0) + 1;
        });
        const signupsByProfession = Object.keys(professionMap).map((key) => ({
          name: key,
          value: professionMap[key],
        }));

        setStats({
          totalVisits: visitsCount || 0,
          uniqueVisitors: uniqueIds.size,
          totalSignups: signupsCount || 0,
          signupsPerVisitor,
          conversionRate,
          conversionRateUnique,
          dailyVisits,
          dailySignups,
          signupsByProfession,
        });
      } catch (err) {
        console.error("Erreur stats :", err);
      }
    };

    fetchStats();
  }, []);

  const COLORS = ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"];

  return (
    <div className="container">
      <header className="hero">
        <h1>Statistiques Malaria-Scan</h1>
      </header>

      <main style={{ padding: "2rem" }}>
        <h2>Résumé</h2>
        <p>Nombre total de visites : {stats.totalVisits}</p>
        <p>Nombre de visiteurs uniques : {stats.uniqueVisitors}</p>
        <p>Nombre total d’inscriptions : {stats.totalSignups}</p>
        <p>Inscriptions par visiteur : {stats.signupsPerVisitor.toFixed(2)}</p>
        <p>
          <strong>
            Taux de conversion : {stats.conversionRate.toFixed(2)}% :
          </strong>{" "}
          Montre le nombre d’inscriptions par rapport à toutes les visites, même
          si plusieurs personnes utilisent la même machine. Donne une idée
          globale de l’efficacité du site
        </p>
        <p>
          <strong>
            Taux de conversion (visiteurs uniques){" "}
            {stats.conversionRateUnique.toFixed(2)}% :
          </strong>{" "}
          Montre le nombre d’inscriptions par machine/navigateur distinct. Utile
          pour voir si une machine “type” convertit, mais peut être trompeur si
          plusieurs personnes utilisent la même machine :{" "}
        </p>

        <h2>Visites et inscriptions par jour</h2>
        <BarChart width={600} height={300} data={stats.dailyVisits}>
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="count" name="Visits" fill="#8884d8" />
        </BarChart>
        <BarChart width={600} height={300} data={stats.dailySignups}>
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="count" name="Signups" fill="#82ca9d" />
        </BarChart>

        <h2>Répartition par profession</h2>
        <PieChart width={400} height={400}>
          <Pie
            data={stats.signupsByProfession}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={100}
            fill="#8884d8"
            label
          >
            {stats.signupsByProfession.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Legend />
        </PieChart>
      </main>
    </div>
  );
}

export default StatsPage;
